/**
 * A candidate or chosen arrangement of partial runs, turned into screen
 * points and a drawable path.
 *
 * `projection.ts` projects **one** run. An outline is a chain over several
 * runs with a question of its own — where does it gape? — so this module
 * sits on top of `projection.ts` and does not replace it: geometry, closure
 * sums and the ``closed`` verdict all still come from the backend.
 */
import { formatMm } from "./format";
import {
  projectRun,
  type MmPoint,
  type Projection,
  type ProjectionOptions,
  type ScreenPoint,
} from "./projection";

// Re-exported so a caller drawing one outline segment (`outlineSegments`
// below) finds the point type here too, without a second import from
// `./projection` next to this module's own types.
export type { ScreenPoint };

/**
 * The five states one drawn wall can carry on its own (spec §2.6). The sixth
 * state the spec names, Widerspruch, is not a wall's own state — it is the
 * outline-level gap Task 8 already draws (`closureGap`).
 *
 * Decided once, in the backend (`app/demo.py`'s `_segment_state`), off the
 * edge's own reading. Nothing in this module re-derives it from
 * `hasGrating`/`gratingPresence` a second time — that pairing already exists
 * on `EdgeOut` for the per-run schema view and answers a different question
 * there (what gets planned), not what a screen paints here.
 */
export type EdgeState =
  | "grating"
  | "grating_unknown"
  | "no_grating"
  | "undimensioned"
  | "interruption"
  | "form_only";

/**
 * Whether a wall is where the extraction put it, or where a person moved it
 * — the backend's own `SegmentOrigin`. A correction moves the drawing, not
 * the reading, and a wall a person put right must not look like one the
 * model got right (A-014), so the two states have to travel with the wall
 * rather than being inferred from anything else on screen.
 */
export type SegmentOrigin = "read" | "user_adjusted";

/** Where a wall's drawn length came from — the backend's `SegmentOut.lengthSource`.
 *
 * Only `"read"` may print a figure. `"closure"` and `"sketch"` are geometry:
 * the wall is that long on screen because the ring or the sketch says so, and
 * printing a number there would put an invented length in front of a person
 * who is about to confirm it (A-014). */
export type LengthSource = "read" | "closure" | "sketch" | "manual";

/**
 * The sources that *state* a length rather than derive one — the mirror of the
 * backend's own `ASSERTED` set (`schema/facade.py`).
 *
 * `read` is a model's word for it, `manual` a person's. Both put a figure on
 * the wall and both make it solid; `closure` and `sketch` are geometry and put
 * nothing there but a line (E-14, A-014).
 */
const ASSERTED: ReadonlySet<LengthSource> = new Set<LengthSource>(["read", "manual"]);

/**
 * One drawn step of an outline, with what is actually on that wall — not
 * just where it runs. Without this a house is one grey line and Chiara's
 * question ("where does the rost actually sit?") stays unanswered.
 */
export interface DemoSegment {
  runId: string;
  edgeId: string;
  state: EdgeState;
  lengthMm: number | null;
  label: string;
  interruption: string | null;
  /**
   * What sits on this wall, where the contour named it — "Tür", "unbemaßt bis
   * zur linken Hauskante" (backend `SegmentOut.note`).
   *
   * Its own field beside `label`, which is the wording of a *measurement*. A
   * wall the contour drew but nobody dimensioned has no such wording, and a
   * dashed line with nothing on it is the one thing the drawing may not be:
   * it cannot be confirmed. Absent on an arrangement of partial runs, which
   * says what is on a wall through `interruption` instead.
   */
  note?: string | null;
  /**
   * `"user_adjusted"` on every wall a correction touched — the one dragged
   * and the neighbours that absorbed it alike (backend `SegmentOut.origin`).
   *
   * Optional here, not on the wire: the backend always sends a value, but
   * the outline-drawing test suite (`outline.test.ts`, `OutlinePicker.test.tsx`,
   * `OutlineReview.test.tsx`) built its fixtures before this field existed,
   * and almost all of them describe a wall nobody has corrected — leaving it
   * out says exactly that, the same thing `undefined` and `"read"` mean to
   * Task 8's highlight condition.
   */
  origin?: SegmentOrigin;
  /**
   * How much this wall's own length changed under a correction, or `null`
   * where nothing touched it (backend `SegmentOut.adjustedDeltaMm`). Zero on
   * the wall that was dragged itself — a drag translates that wall
   * perpendicular to itself, so its own length is unchanged and only its
   * neighbours' lengths move; the distance the dragged wall travelled lives
   * on `DemoRunResult.adjustments` instead. Optional for the reason `origin`
   * above is.
   */
  adjustedDeltaMm?: number | null;
  /** See {@link LengthSource}. Optional because the outline suites' fixtures
   * predate the field; absent means `"read"`, which is what an arrangement of
   * partial runs always is. */
  lengthSource?: LengthSource;
  /** What the wall is drawn as, in mm — set even where `lengthMm` is null. */
  drawLengthMm?: number;
  /** This wall ends where the drawing was cut off (`open_ends`). */
  openEnd?: boolean;
  /**
   * This wall's own identity in the editable document (backend
   * `SegmentOut.wallId` — `DocumentWall.id`, `w1`, `w2`, …).
   *
   * The handle every geometry op is keyed by. **Not `edgeId`**, which is a
   * display artefact and never a document key (design §4): two runs may
   * answer to the same id and then build the same edge ids too, and an edit
   * does not carry it across the way this is carried.
   *
   * `null`/absent wherever there is no document behind this wall — the
   * enumeration path, and a contour whose document could not be built. A
   * corner or facade-object gesture on such a wall has nothing to name and
   * is refused rather than posted under a made-up id: a wrong `wallId`
   * fails at replay, which bricks the case rather than losing one gesture.
   */
  wallId?: string | null;
  /**
   * What the drawing said for this wall before a person typed over it
   * (backend `SegmentOut.displacedReadingMm`, E-46) — kept so the typed
   * figure never hides the read one. `null`/absent where nothing was
   * displaced.
   */
  displacedReadingMm?: number | null;
  /**
   * `"fase"` on the slant a chamfer cut into the outline (backend
   * `SegmentOut.kind`, design 2026-10-18 §6.2) — a wall of its own, with its
   * own number, though the document holds none; its `wallId` is the
   * synthetic `{wall}~fase` and no op may name it. Absent means `"wall"`, as
   * every fixture written before chamfers existed says.
   */
  kind?: SegmentKind;
  /**
   * On a wall a chamfer shortened: the customer's measure up to the imagined
   * corner point (backend `SegmentOut.cornerPointLengthMm`). `lengthMm` is
   * then the stretch a grating can lie on — what the board and the planner
   * count on — and this is what the person stated, shows in „Maß“ and
   * changes there (design §5.1). `null`/absent on every other wall.
   */
  cornerPointLengthMm?: number | null;
  /**
   * The figure somebody stated for this wall that the sketch contradicts by
   * more than a factor of five (backend `SegmentOut.implausibleLengthMm`,
   * E-64). The wall is then drawn dashed at the sketch's length and carries
   * no `lengthMm` until a person decides — this is the figure in question,
   * never a length to plan with (A-014). `null`/absent on every other wall.
   */
  implausibleLengthMm?: number | null;
}

/** An ordinary wall, or the slant a chamfer put into the outline. */
export type SegmentKind = "wall" | "fase";

/** Whether this step is a chamfer's slant (design 2026-10-18). */
export function isFase(segment: Pick<DemoSegment, "kind"> | null | undefined): boolean {
  return segment?.kind === "fase";
}

/**
 * Whether the corner at the **end** of wall `wallIndex` is one of a
 * chamfer's two corners — the slant's own end, or the end of the wall the
 * slant follows. The key `corners[]` and the corner menu use (design
 * 2026-10-18 §5.2, call 7): such a corner is only ever butted.
 *
 * The corner after the last wall is the ring's seam; an open chain has none.
 */
export function isFaseCorner(outline: DemoOutline, wallIndex: number): boolean {
  const count = outline.segments.length;
  if (wallIndex < 0 || wallIndex >= count) return false;
  if (isFase(outline.segments[wallIndex])) return true;
  const next = wallIndex + 1;
  if (next < count) return isFase(outline.segments[next]);
  return outline.closed && isFase(outline.segments[0]);
}

/** „An einer Fase …“: why a chamfer's corner offers no mitre and no filling. */
export const FASE_BUTT_ONLY = "An einer Fase gibt es nur den stumpfen Stoß.";
/** Why nothing stands on a chamfer's slant and no hole goes into it. */
export const FASE_NO_OBJECTS = "Auf einer Fase gibt es keine Objekte und Ausschnitte.";
/** Where the slant's own measure is changed — not on the board (design §5.2, call 2). */
export const FASE_MEASURE = "Maß aus der Fase — ändern über „Form ändern …“";

/**
 * What a wall a chamfer shortened says about itself, exactly as the design
 * words it (2026-10-18 §5.1): „2.500 mm belegbar (3.000 bis zum Eckpunkt)“ —
 * the stretch a grating lies on, and the customer's measure up to the
 * imagined corner point. `null` on every wall that is not shortened.
 */
export function shortenedWallLabel(segment: Pick<DemoSegment, "lengthMm" | "cornerPointLengthMm">): string | null {
  const { lengthMm, cornerPointLengthMm } = segment;
  if (lengthMm === null || cornerPointLengthMm == null || cornerPointLengthMm === lengthMm) return null;
  return `${formatMm(lengthMm)} belegbar (${formatMm(cornerPointLengthMm).replace(/\s*mm$/, "")} bis zum Eckpunkt)`;
}

/** One arrangement of the partial runs, as the demo endpoint hands it over. */
export interface DemoOutline {
  id: string;
  connected: boolean;
  nodes: { xMm: number; yMm: number }[];
  sumHorizontalMm: number;
  sumVerticalMm: number;
  gapMm: number;
  /**
   * Whether this arrangement is a ring — decided in the backend's `schema`
   * module off the geometry, not re-derived here from `gapMm === 0`. The
   * unconnected arrangement also reports a gap of zero as an unclaimed
   * placeholder, without being closed.
   */
  closed: boolean;
  /**
   * Whether `gapMm` and the two sums say anything about this building
   * (backend `OutlineOut.closureEvaluable`, `schema.facade`, spec §5.4) —
   * exactly whether the contour closes. `false` on an open contour, whatever
   * fraction of its walls were read; a closed contour whose own read walls
   * contradict each other stays evaluable, with the check itself failing
   * (backend `facade_closure_conflict`) — clearing this flag there would
   * hide the one finding that case exists to surface.
   *
   * Optional here, not on the wire — the backend always sends it, and the
   * fixtures of the outline suites predate the field. Absent means evaluable,
   * which is what every arrangement of partial runs is.
   */
  closureEvaluable?: boolean;
  corners: number;
  chosen: boolean;
  /**
   * Every drawn step of this outline, in drawing order — `nodes.length - 1`
   * of them for a connected outline, none for the unconnected arrangement
   * (the backend's `OutlineCandidate.segments` carries the same invariant).
   * What is *on* the wall lives here; `nodes` only says where it runs.
   */
  segments: DemoSegment[];
}

/**
 * The finding rule: a stated figure the sketch contradicts by more than a
 * factor of five (E-64). Named here because the board, the Form-ändern
 * dialog and the shape editor all read it off the same plan answer.
 */
export const MEASURE_IMPLAUSIBLE = "measure_implausible";

/** Something about the chosen arrangement that does not match what was read. */
export interface DemoFinding {
  rule: string;
  message: string;
  measurementIds: string[];
  /**
   * Which laid elements this concerns — planning path only (backend
   * `FindingOut.placement_ids`).
   *
   * Its own field beside `measurementIds`, and the distinction is load-bearing:
   * a placement is not a reading, and the two open different things — one the
   * page at a box, the other an element on the board. A screen reading the
   * wrong one gets an empty list and marks nothing, which is what happened
   * here until 16.08.2026: the violation state existed, was tested against a
   * hand-built fixture, and could not occur in the running program.
   */
  placementIds?: string[];
  /** Zero-based; the wall this finding is about, `undefined` where it is about
   * none. Without it a screen has to fish the number out of the German
   * sentence, which is not a thing to keep. */
  wallIndex?: number;
  /**
   * `"blocking"` — this cannot be built as drawn, a release may not step over
   * it — or `"hint"` (backend `FindingOut.severity`, `planning.BLOCKING_RULES`,
   * design 2026-09-13 §8). Optional here, not on the wire: the outline
   * suites' fixtures predate it. The print documents list the blocking ones.
   */
  severity?: string;
  /**
   * On `measure_implausible` (E-64): each one-place comma shift of the stated
   * figure that fits the sketch, in mm, ascending — offered, never applied
   * (E-16). Empty or absent where no shift fits.
   */
  suggestedLengthsMm?: number[];
}

/**
 * The outline's nodes as `projection.ts` wants them: points with ids.
 *
 * The backend's nodes carry no id of their own — `OutlineOut.nodes` is a
 * bare `{ xMm, yMm }[]` — so the id is invented from the position. That is
 * safe here because a candidate's node order is fixed drawing order, not
 * something a caller reshuffles between reading `outline.nodes[i]` and
 * reading `projection.points["o-n" + i]`.
 */
function points(outline: DemoOutline): MmPoint[] {
  return outline.nodes.map((node, index) => ({
    id: `o-n${index}`,
    xMm: node.xMm,
    yMm: node.yMm,
  }));
}

/**
 * The whole outline in the box, at one scale for both axes.
 *
 * Both axes share a scale on purpose: stretching each axis separately to
 * fill the box would draw a squashed house, and the shape is the error
 * detector (E-13) — a shape that lies about its own proportions detects
 * nothing. `projectRun` fits that one scale over every node it is handed,
 * and it is handed the whole outline here, not one run of it.
 *
 * This used to fit the scale itself and hand it in through a `scale` option.
 * Both were removed: the scale was fitted over the same node list that was
 * then passed in, so it recomputed what `projectRun` computes anyway (review
 * finding, 10.08.2026).
 */
export function outlineProjection(outline: DemoOutline, options: ProjectionOptions): Projection {
  return projectRun(points(outline), options);
}

/** The projected outline in drawing order, ready for one SVG `path`. */
export function outlinePath(outline: DemoOutline, options: ProjectionOptions): string {
  const projection = outlineProjection(outline, options);
  return outline.nodes
    .map((_node, index) => {
      // Record access, not array access — noUncheckedIndexedAccess is right
      // that this could be missing in general, but `points()` above just
      // built one entry per index of this same `outline.nodes`.
      const point = projection.points[`o-n${index}`]!;
      return `${index === 0 ? "M" : "L"}${point.x},${point.y}`;
    })
    .join(" ");
}

/**
 * Each drawn step with its two endpoints, so a wall can be drawn as itself —
 * a coloured, hatched or dashed line instead of the one grey path
 * `outlinePath` produces.
 *
 * Empty for the unconnected arrangement and for a candidate the backend
 * built with no steps of its own (mirrors `outlinePath`'s and
 * `outlineProjection`'s own indifference to `connected`, but here the guard
 * matters: walking zero segments is correct, walking node pairs that were
 * never claimed as a step would not be).
 */
export function outlineSegments(
  outline: DemoOutline,
  options: ProjectionOptions,
): { segment: DemoSegment; from: ScreenPoint; to: ScreenPoint }[] {
  if (!outline.connected || outline.segments.length === 0) return [];
  const projection = outlineProjection(outline, options);
  return outline.segments.map((segment, index) => ({
    segment,
    // Non-null like `outlinePath`'s own access above: the backend guarantees
    // `segments.length === nodes.length - 1` (`OutlineCandidate.segments`'s
    // docstring), so index and index + 1 both name a node `points()` built.
    from: projection.points[`o-n${index}`]!,
    to: projection.points[`o-n${index + 1}`]!,
  }));
}

/**
 * How far the outline misses its own starting point, in millimetres — the
 * vector from the last node to the first.
 *
 * `null` when it already closes, and `null` for the unconnected
 * arrangement — a set of separate bars has no gap to show, and drawing one
 * would invent a verdict where the specification says none can be given
 * (E-15).
 *
 * Reads `sumHorizontalMm`/`sumVerticalMm` off the outline instead of
 * re-deriving them from `nodes[0]` and the last node (review finding,
 * 10.08.2026): the backend already computed Σh/Σv once, in
 * `schema/outline.py`. A second, independent computation of the same figure
 * at a place that is only supposed to display it is the authority split
 * Task 5 removed from `demo.py` for `closed` — one layer further out, and
 * just as able to drift silently: once E-14 work on partially dimensioned
 * runs puts a placeholder position into `nodes` that does not count towards
 * the sums, this and the backend would disagree and nothing would catch it.
 */
export function closureGap(outline: DemoOutline): { xMm: number; yMm: number } | null {
  if (!outline.connected || outline.gapMm === 0) return null;
  // An open contour has nothing to evaluate, whatever fraction of its walls
  // were read — the backend says so (`closureEvaluable`) off the contour's
  // own `closed` flag, not off this distance, and not off a placeholder that
  // no longer exists. Drawing the gap anyway would put an invented length on
  // screen as a measured one (E-15). A closed contour whose read walls
  // contradict each other does *not* reach this branch — it stays evaluable
  // with the check itself failing (`facade_closure_conflict`), which is the
  // one finding this line must not swallow.
  if (outline.closureEvaluable === false) return null;
  return { xMm: -outline.sumHorizontalMm, yMm: -outline.sumVerticalMm };
}

/**
 * What to say about the Restklaffmaß — read off `closed`, never off
 * `gapMm === 0`. Those two agree for almost every shape but not for a
 * folded-back run, which reaches `gapMm = 0` without enclosing an area; that
 * is exactly why `closed` exists as its own field, derived in the backend
 * from a shoelace check rather than from this distance (Task 5).
 *
 * A domain function like `closureGap`/`outlinePath` above it, so it lives
 * here rather than in whichever feature component happened to need it
 * first — moved out of `OutlinePicker.tsx` (Task 8, fix round 1) once a
 * second component (`OutlineReview`) needed to say the same thing about the
 * same outline, which would otherwise have re-derived it a fifth time next
 * to a differently drawn copy of the shape.
 */
export function closureText(outline: DemoOutline): string {
  if (!outline.connected) return "keine Prüfung möglich";
  if (outline.closureEvaluable === false) return "Schließung nicht prüfbar";
  if (outline.closed) return "schließt";
  return `${formatMm(outline.gapMm)} offen`;
}

/**
 * The run id the backend gives every wall of a facade contour
 * (`schema.facade.FACADE_RUN_ID`). A contour is drawn like any other connected
 * shape, so this is the one thing that tells the two apart on screen.
 */
export const FACADE_RUN_ID = "facade";

/** Whether this shape is a contour the model described rather than an
 * arrangement of partial runs that was enumerated. */
export function isContour(outline: DemoOutline): boolean {
  return (
    outline.segments.length > 0 &&
    outline.segments.every((segment) => segment.runId === FACADE_RUN_ID)
  );
}

/**
 * Whether a wall's own reading — not the ring's arithmetic, not the sketch's
 * proportion — is what `lengthMm` holds. The governing rule (A-014): only
 * `lengthSource === "read"` may print a figure, absent meaning `"read"` for
 * the reason {@link DemoSegment.lengthSource} documents.
 *
 * The one place this check is written. `dimensionMark`, `isPartial` and
 * `OutlineReview.tsx`'s own `Wall`/`WallGrip` all defer to it rather than
 * re-deriving `segment.lengthMm != null && (segment.lengthSource ?? "read")
 * === "read"` at each call site — a screen reader's `aria-valuetext` is as
 * much "printing a figure" as the drawn arrow is, and a rule checked in one
 * place cannot drift from itself at a second (fix round 1, review finding).
 */
export function isSegmentRead(segment: DemoSegment): boolean {
  return carriesMeasure(segment);
}

/**
 * The same rule for anything shaped like a wall — a length and where it came
 * from — so a reader that holds no whole {@link DemoSegment} (the axis hint
 * on the board, `features/planning/axisGap.ts`) asks this one place instead of
 * writing the set a second time.
 */
export function carriesMeasure(wall: { lengthMm: number | null; lengthSource?: string | null }): boolean {
  return wall.lengthMm != null && ASSERTED.has((wall.lengthSource ?? "read") as LengthSource);
}

/**
 * What a wall carries, in the person's word (spec 2026-10-15 §2): „gelesen“
 * off the drawing, „von dir“ where somebody typed it, „ohne Maß“ for
 * everything else — a sketched wall and one the ring forced alike, since
 * neither states a length. The same three words on the board's „Maß“ field
 * and in the outline editor seeded from a Stand (Task 7).
 */
export type MeasureWord = "gelesen" | "von dir" | "ohne Maß";

export function measureWord(wall: { lengthMm: number | null; lengthSource?: string | null }): MeasureWord {
  if (!carriesMeasure(wall)) return "ohne Maß";
  return (wall.lengthSource ?? "read") === "manual" ? "von dir" : "gelesen";
}

/**
 * The dimension arrow for one wall, offset beside it — or `null` where there
 * is no reading to show.
 *
 * Beside, not on: the arrow has to visibly span the same stretch as the wall,
 * which is the moment E-13 works. A label floating next to a line leaves the
 * reader checking a list of figures again.
 */
export function dimensionMark(
  segment: DemoSegment,
  from: ScreenPoint,
  to: ScreenPoint,
  offsetPx: number,
): { line: string; ticks: string; labelX: number; labelY: number; rotate: number } | null {
  if (!isSegmentRead(segment)) return null;

  const horizontal = Math.abs(to.x - from.x) >= Math.abs(to.y - from.y);
  const dx = horizontal ? 0 : offsetPx;
  const dy = horizontal ? offsetPx : 0;
  const a = { x: from.x + dx, y: from.y + dy };
  const b = { x: to.x + dx, y: to.y + dy };

  const tick = 4;
  const ticks = horizontal
    ? `M${a.x},${a.y - tick} L${a.x},${a.y + tick} M${b.x},${b.y - tick} L${b.x},${b.y + tick}`
    : `M${a.x - tick},${a.y} L${a.x + tick},${a.y} M${b.x - tick},${b.y} L${b.x + tick},${b.y}`;

  return {
    line: `M${a.x},${a.y} L${b.x},${b.y}`,
    ticks,
    labelX: (a.x + b.x) / 2,
    labelY: (a.y + b.y) / 2,
    rotate: horizontal ? 0 : -90,
  };
}

/** Whether the heading must say "Teilrekonstruktion" — any wall without a
 * reading of its own. */
export function isPartial(outline: DemoOutline): boolean {
  return outline.segments.some((segment) => !isSegmentRead(segment));
}
